import React, { useEffect, useState, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SEO from './SEO';
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Snackbar,
  Alert,
  TextField,
  Paper,
  Container,
  Fade,
  Grow
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EmailIcon from '@mui/icons-material/Email';
import AuthContext from '../context/AuthContext';

const VerifyEmail = () => {
    const [loading, setLoading] = useState(false);
    const [verified, setVerified] = useState(false);
    const [error, setError] = useState(null);
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [resendLoading, setResendLoading] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { backendUrl } = useContext(AuthContext);

    useEffect(() => {
        const emailParam = searchParams.get('email');
        if (emailParam) {
            setEmail(emailParam);
        }
    }, []);

    const handleVerify = async (e) => {
        e.preventDefault();
        setError(null);

        if (!email || !code) {
            setError('Please enter your email and verification code');
            return;
        }

        if (code.length !== 5) {
            setError('Please enter the 5-digit verification code');
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(`${backendUrl}api/auth/verify-email-code/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, code }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Verification failed');
            }

            setVerified(true);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        if (!email) {
            setError('Please enter your email address first');
            return;
        }

        setResendLoading(true);
        setError(null);
        try {
            const response = await fetch(`${backendUrl}api/auth/resend-email-code/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || errorData.detail || 'Failed to resend code');
            }

            setSnackbarMessage('A new verification code has been sent to your email.');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
            setCode('');
        } catch (err) {
            setSnackbarMessage(err.message);
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } finally {
            setResendLoading(false);
        }
    };

    const handleSnackbarClose = () => setSnackbarOpen(false);

    if (verified) {
        return (
            <Container maxWidth="sm">
                <Box
                    sx={{
                        minHeight: '100vh',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        py: 4,
                    }}
                >
                    <Fade in={true} timeout={800}>
                        <Paper
                            elevation={3}
                            sx={{
                                p: 5,
                                textAlign: 'center',
                                borderRadius: 3,
                                width: '100%',
                                maxWidth: 500,
                                background: '#FFFFFF',
                            }}
                        >
                            <Grow in={true} timeout={600}>
                                <Box>
                                    <Box
                                        sx={{
                                            display: 'inline-flex',
                                            p: 2,
                                            borderRadius: '50%',
                                            background: '#7B6B8D',
                                            mb: 3,
                                        }}
                                    >
                                        <CheckCircleOutlineIcon sx={{ fontSize: 80, color: 'white' }} />
                                    </Box>

                                    <Typography variant="h3" gutterBottom sx={{ fontWeight: 600, color: '#7B6B8D', mb: 2 }}>
                                        Success!
                                    </Typography>

                                    <Typography variant="h5" gutterBottom sx={{ color: 'text.primary', fontWeight: 500, mb: 2 }}>
                                        Email Verified
                                    </Typography>

                                    <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary', lineHeight: 1.7 }}>
                                        Your email has been successfully verified! You can now access all features of Couplendar.
                                    </Typography>

                                    <Button
                                        variant="contained"
                                        size="large"
                                        onClick={() => navigate('/login')}
                                        sx={{
                                            px: 5,
                                            py: 1.5,
                                            fontSize: '1.1rem',
                                            fontWeight: 600,
                                            background: '#7B6B8D',
                                            boxShadow: '0 4px 15px rgba(123, 107, 141, 0.3)',
                                            '&:hover': {
                                                background: '#6A5A7A',
                                                boxShadow: '0 6px 20px rgba(123, 107, 141, 0.4)',
                                            },
                                        }}
                                    >
                                        Continue to Login
                                    </Button>
                                </Box>
                            </Grow>
                        </Paper>
                    </Fade>
                </Box>
            </Container>
        );
    }

    return (
        <>
        <SEO title="Verify Email - Couplendar" />
        <Container maxWidth="sm">
            <Box
                sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    py: 4,
                }}
            >
                <Fade in={true} timeout={800}>
                    <Paper
                        elevation={3}
                        sx={{
                            p: 5,
                            textAlign: 'center',
                            borderRadius: 3,
                            width: '100%',
                            maxWidth: 500,
                            background: '#FFFFFF',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'inline-flex',
                                p: 2,
                                borderRadius: '50%',
                                background: '#7B6B8D',
                                mb: 3,
                            }}
                        >
                            <EmailIcon sx={{ fontSize: 60, color: 'white' }} />
                        </Box>

                        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
                            Check Your Email
                        </Typography>

                        <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary', lineHeight: 1.7 }}>
                            We've sent a 5-digit verification code to your email. Enter it below to activate your account.
                            {' '}If you don't see it, please check your spam or junk folder.
                        </Typography>

                        <Box component="form" onSubmit={handleVerify} sx={{ textAlign: 'left' }}>
                            {!searchParams.get('email') && (
                                <TextField
                                    fullWidth
                                    margin="normal"
                                    label="Email Address"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    disabled={loading}
                                    sx={{ mb: 2 }}
                                    InputProps={{ sx: { borderRadius: 2 } }}
                                />
                            )}

                            <TextField
                                fullWidth
                                margin="normal"
                                label="5-digit verification code"
                                value={code}
                                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
                                disabled={loading}
                                inputProps={{ maxLength: 5, inputMode: 'numeric', style: { letterSpacing: '0.5em', fontSize: '1.4rem', textAlign: 'center' } }}
                                sx={{ mb: 3 }}
                                InputProps={{ sx: { borderRadius: 2 } }}
                            />

                            {error && (
                                <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                                    {error}
                                </Alert>
                            )}

                            <Button
                                type="submit"
                                variant="contained"
                                size="large"
                                fullWidth
                                disabled={loading || code.length !== 5}
                                sx={{
                                    py: 1.5,
                                    fontSize: '1.1rem',
                                    fontWeight: 600,
                                    background: '#7B6B8D',
                                    boxShadow: '0 4px 15px rgba(123, 107, 141, 0.3)',
                                    '&:hover': {
                                        background: '#6A5A7A',
                                        boxShadow: '0 6px 20px rgba(123, 107, 141, 0.4)',
                                    },
                                }}
                            >
                                {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Verify Email'}
                            </Button>

                            <Box sx={{ mt: 3, textAlign: 'center' }}>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    Didn't receive a code?{' '}
                                    <Button
                                        variant="text"
                                        size="small"
                                        onClick={handleResend}
                                        disabled={resendLoading}
                                        sx={{ color: '#7B6B8D', fontWeight: 600, p: 0, minWidth: 'auto', verticalAlign: 'baseline' }}
                                    >
                                        {resendLoading ? 'Sending...' : 'Resend code'}
                                    </Button>
                                </Typography>
                            </Box>
                        </Box>
                    </Paper>
                </Fade>

                <Typography variant="body2" sx={{ mt: 4, color: 'text.secondary', fontStyle: 'italic' }}>
                    Couplendar - Bringing couples closer, one day at a time
                </Typography>
            </Box>

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity={snackbarSeverity}
                    variant="filled"
                    sx={{ width: '100%', borderRadius: 2 }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Container>
        </>
    );
};

export default VerifyEmail;
